import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Button } from "semantic-ui-react";
import LibraryCardContent from "./LibraryCardContent/LibraryCardContent";
import BackCardContent from "./LibraryCardContent/BackCardContent";
import PDFDownloader from "./PDFDownloader"; // Import the new component
import { profileApi } from "@api/profile/profile";
import { configurableApi } from "@api/configurables/configurables";
import { getFullImageUrl, MOBILE_BREAKPOINT } from "@utils/mobileUtils";

const STYLES = {
  card: {
    width: 700,
    height: 500,
    border: "2px solid #333",
    borderRadius: 8,
    background: "#f8f9fa",
    fontFamily: "Arial, sans-serif",
    position: "relative",
    overflow: "hidden",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    padding: "20px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 20,
  },
  leftLogo: {
    width: 120,
    height: 120,
    objectFit: "contain",
    borderRadius: "50%",
  },
  rightLogo: {
    width: 120,
    height: 120,
    objectFit: "contain",
    borderRadius: "50%",
  },
  universityInfo: { textAlign: "center", flex: 1, margin: "0 10px" },
  universityName: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#333",
    margin: 0,
    letterSpacing: "0.5px",
  },
  universityLocation: {
    fontSize: 14,
    color: "#666",
    margin: "2px 0 8px 0",
    fontWeight: "800",
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#333",
    margin: 0,
    letterSpacing: "1px",
  },
  mainContent: { display: "flex", flex: 1, gap: "30px" },
  leftSection: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  rightSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "20px",
  },
  fieldGroup: { marginBottom: 25 },
  fieldLabel: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#333",
    marginBottom: 8,
    letterSpacing: "0.5px",
  },
  fieldLine: {
    borderBottom: "1.5px solid #333",
    height: 24,
    minHeight: 24,
    display: "flex",
    alignItems: "flex-end",
    paddingBottom: 2,
  },
  fieldValue: {
    fontSize: 18,
    color: "#000",
    fontWeight: "500",
    width: "100%",
    minHeight: 20,
    display: "flex",
    alignItems: "flex-end",
    paddingBottom: 2,
    letterSpacing: "0.5px",
  },
  photoContainer: {
    width: 160,
    height: 160,
    border: "2px solid #333",
    background: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    color: "#666",
    fontWeight: "bold",
    overflow: "hidden",
    marginTop: 45,
  },
  photoImage: { width: "100%", height: "100%", objectFit: "cover" },
  librarianSection: { textAlign: "center", marginTop: "auto" },
  librarianName: {
    fontWeight: "bold",
    color: "#333",
    textAlign: "center",
  },
  librarianTitle: {
    fontSize: 14,
    color: "#666",
    margin: "2px 0 0 0",
    letterSpacing: "0.3px",
    fontWeight: "600",
  },
  buttonContainer: { textAlign: "center", marginTop: 16 },
  actionButton: {
    margin: "0 5px",
    padding: "8px 16px",
    borderRadius: 4,
    cursor: "pointer",
    fontSize: 14,
    border: "none",
  },
  downloadButton: { background: "#2185d0", color: "#fff" },
  disabledButton: {
    background: "#cccccc",
    color: "#666",
    cursor: "not-allowed",
  },
  barcode: {
    display: "block",
    margin: "0 auto",
    padding: "8px 0",
    backgroundColor: "fff",
    border: "1px solid #ddd",
    borderRadius: "4px",
    maxWidth: "fit-content",
  },
  loadingPhoto: {
    fontSize: 12,
    color: "#666",
    fontStyle: "italic",
  },
  errorPhoto: {
    fontSize: 12,
    color: "#d32f2f",
    textAlign: "center",
  },
  note: {
    fontSize: 18,
    fontStyle: "italic",
    fontWeight: "500",
    color: "#666",
    margin: "8px auto 0 auto",
    letterSpacing: "0.3px",
    textAlign: "center",
    maxWidth: "400px",
    whiteSpace: "normal",
    lineHeight: 1.4,
  },
  noteMobile: {
    fontSize: 12,
    fontStyle: "italic",
    fontWeight: "500",
    color: "#666",
    margin: "8px auto 0 auto",
    letterSpacing: "0.3px",
    textAlign: "center",
    maxWidth: "250px",
    whiteSpace: "normal",
    lineHeight: 1.4,
  },
};

const LibraryCard = ({ user = {}, currentUser = {} }) => {
  const cardRef = useRef(null);
  const contentRef = useRef(null);
  const [showBack, setShowBack] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(null);

  // Initialize with empty string instead of hardcoded name
  const [librarianName, setLibrarianName] = useState("");
  const [librarianLoading, setLibrarianLoading] = useState(false);

  // Function to check if current user is a librarian
  const isLibrarian = () => {
    // Check various possible ways to identify a librarian
    const userRole =
      currentUser.role || currentUser.user_type || currentUser.type;
    const userRoles = currentUser.roles || [];

    return (
      userRole === "librarian" ||
      userRole === "Librarian" ||
      userRole === "LIBRARIAN" ||
      userRoles.includes("librarian") ||
      userRoles.includes("Librarian") ||
      userRoles.includes("LIBRARIAN") ||
      currentUser.is_librarian === true ||
      currentUser.isLibrarian === true
    );
  };

  // Fixed librarian name fetching
  const fetchLibrarianName = async () => {
    try {
      setLibrarianLoading(true);

      const response = await configurableApi.getValue("Library Name");

      // Handle the API response correctly - the value is directly in response.data
      if (response && response.data) {
        const nameValue = response.data;

        if (nameValue && typeof nameValue === "string" && nameValue.trim()) {
          setLibrarianName(nameValue.trim());
        }
      }
    } catch (error) {
      // Silent error handling
    } finally {
      setLibrarianLoading(false);
    }
  };

  const fetchProfileImage = async () => {
    try {
      setImageLoading(true);
      setImageError(null);

      // Case 1: image_url is already provided (from PatronHeader)
      if (user.image_url) {
        const fullImageUrl = getFullImageUrl(user.image_url);
        setProfileImage(fullImageUrl);
        return;
      }

      // Case 2: image_url not provided but user.id exists (from Profile page)
      if (user.id) {
        try {
          const imageInfo = await profileApi.getUserImageInfo(user.id);

          if (imageInfo && imageInfo.image_url) {
            const fullImageUrl = getFullImageUrl(imageInfo.image_url);
            setProfileImage(fullImageUrl);
          } else {
            setProfileImage(null);
          }
        } catch (apiError) {
          setProfileImage(null);
        }
        return;
      }

      setProfileImage(null);
    } catch (error) {
      setImageError(error.message || "Failed to load profile image");
      setProfileImage(null);
    } finally {
      setImageLoading(false);
    }
  };

  useEffect(() => {
    fetchProfileImage();
    fetchLibrarianName();
  }, [user.image_url]);

  const renderPhoto = () => {
    if (imageLoading) {
      return <div style={STYLES.loadingPhoto}>Loading photo...</div>;
    }

    if (imageError || !profileImage) {
      return (
        <div style={STYLES.errorPhoto}>
          <div>NO PHOTO</div>
          <div style={{ fontSize: "10px", marginTop: "4px", color: "#d32f2f" }}>
            No Uploaded Photo
          </div>
        </div>
      );
    }

    return (
      <img
        src={profileImage}
        alt={user.full_name || "Student"}
        style={STYLES.photoImage}
        crossOrigin="anonymous"
        onError={(e) => {
          e.target.style.display = "none";
          e.target.parentNode.innerHTML = `
          <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; font-size: 14px; color: #666; font-weight: bold;">
            <div>NO PHOTO</div>
            <div style="font-size: 10px; margin-top: 4px; color: #d32f2f;">No Uploaded Photo</div>
          </div>
        `;
        }}
      />
    );
  };

  const renderLogo = (src, alt, style, onError) => (
    <img
      src={src}
      alt={alt}
      style={style}
      crossOrigin="anonymous"
      onError={onError}
    />
  );

  useEffect(() => {
    const updateCardOrientation = () => {
      if (window.innerWidth <= MOBILE_BREAKPOINT) {
        const scaleValue = window.innerHeight / 2050;
        cardRef.current.style.transform = `scale(${scaleValue})`;
        cardRef.current.style.transformOrigin = "center center";
        cardRef.current.style.margin = "-125px 0 -125px 0";
        cardRef.current.style.display = "block";
        cardRef.current.style.width = "700px";
        cardRef.current.style.height = "500px";
      } else {
        cardRef.current.style.transform = "none";
        cardRef.current.style.width = `${STYLES.card.width}px`;
        cardRef.current.style.height = `${STYLES.card.height}px`;
      }
    };

    updateCardOrientation();
    window.addEventListener("resize", updateCardOrientation);
    return () => window.removeEventListener("resize", updateCardOrientation);
  }, []);

  // Get the librarian name to display
  const getLibrarianName = () => {
    if (librarianLoading) {
      return "Loading...";
    }

    // Return the librarian name from API, or fallback if empty
    return librarianName || "UNIVERSITY LIBRARIAN";
  };

  return (
    <div style={{ padding: "0 20px" }}>
      <div ref={cardRef} style={STYLES.card}>
        {showBack ? (
          <BackCardContent ref={contentRef} user={user} STYLES={STYLES} />
        ) : (
          <LibraryCardContent
            ref={contentRef}
            user={user}
            STYLES={STYLES}
            renderLogo={renderLogo}
            renderPhoto={renderPhoto}
            librarianName={getLibrarianName()}
            librarianTitle={user.librarianTitle || "UNIVERSITY LIBRARIAN"}
          />
        )}
      </div>
      <div>
        <p
          style={
            window.innerWidth <= MOBILE_BREAKPOINT
              ? STYLES.noteMobile
              : STYLES.note
          }
        >
          "<b>Note</b>: This library card is valid only with the librarian's
          signature."
        </p>
      </div>
      <div style={STYLES.buttonContainer}>
        <Button
          style={{
            ...STYLES.actionButton,
            background: showBack ? "#db2828" : "#2185d0",
            color: "#fff",
          }}
          onClick={() => setShowBack((prev) => !prev)}
        >
          {showBack ? "Show Front" : "Show Back"}
        </Button>

        {/* Conditionally render the Download PDF button only for librarians */}
        {isLibrarian() && (
          <PDFDownloader
            contentRef={contentRef}
            cardRef={cardRef}
            user={user}
            showBack={showBack}
            setShowBack={setShowBack}
            STYLES={STYLES}
            buttonStyle={STYLES.actionButton}
          />
        )}
      </div>
    </div>
  );
};

LibraryCard.propTypes = {
  user: PropTypes.shape({
    full_name: PropTypes.string,
    library_no: PropTypes.string,
    department: PropTypes.string,
    signature: PropTypes.string,
    photoUrl: PropTypes.string,
    image_url: PropTypes.string,
    librarianName: PropTypes.string,
    librarianTitle: PropTypes.string,
    creation_date: PropTypes.string,
    lrc_no: PropTypes.string,
    lc_no: PropTypes.string,
  }),
  currentUser: PropTypes.shape({
    role: PropTypes.string,
    user_type: PropTypes.string,
    type: PropTypes.string,
    roles: PropTypes.array,
    is_librarian: PropTypes.bool,
    isLibrarian: PropTypes.bool,
  }),
};

export default LibraryCard;
